'use client';

import { GirlLocation } from '@/components/girl/girl-location';
import { GirlPrices } from '@/components/girl/girl-prices';
import dynamic from 'next/dynamic';
const RequestModal = dynamic(() => import('./request-modal'));

import { GirlAbout } from '@/components/girl/girl-about';
import { GirlGos } from '@/components/girl/girl-gos';
import { GirlInterview } from '@/components/girl/girl-interview';
import { GirlReviews } from '@/components/girl/girl-reviews';
import { GirLSlideshow } from '@/components/girl/girl-slideshow';
import LikeProvider from '@/components/likes/like.provider';
import reviewContract from '@bells/api/review/frontend';
import { cn } from '@bells/core/lib/utils';
import { generateRestClient } from '@bells/core/rest/react';
import {
  attendantAboutTitle,
  attendantFeeTitle,
  attendantGalleryTitle,
  attendantInterviewTitle,
  attendantPreferencesTitle,
  attendantReviews,
  attendantTravelCostsTitle,
  homeNewcomer,
} from '@bells/i18n';
import {
  AttendantProfileState,
  AttendantSearchSingleResponse,
} from '@bells/schema/models/attendant';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@bells/ui/components/tabs';
import { usePwaRouter } from '@bells/ui/utils/use-pwa';
import { ClientInferResponses } from '@ts-rest/core';
import { useCallback, useEffect, useState } from 'react';
import { GirlDesktopHeader } from './girl-desktop-header';
import { usePathname } from 'next/navigation';
import { isEmpty } from 'lodash';
import { useAuth } from '@bells/core/auth';
import { AttendantReviewResponse } from '@bells/schema/models/review';
import reviewPrivateContract from '@bells/api/review/private';
import { Badge } from '@bells/ui/components/badge';

const ReviewEditModal = dynamic(() => import('./review-edit-modal'), {
  ssr: false,
});

const reviewClient = generateRestClient(reviewContract);
const reviewPrivateClient = generateRestClient(reviewPrivateContract);

const GirlLayoutInternal = ({
  data,
  reviewData: initReviewData,
}: {
  data?: AttendantSearchSingleResponse;
  reviewData?: ClientInferResponses<typeof reviewContract.getReviews, 200>;
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const isPwa = usePwaRouter();
  const tsr = reviewClient.useQueryClient();

  const { isAuthed } = useAuth();

  const { data: reviewsData, isLoading } = reviewClient.getReviews.useQuery({
    queryData: { params: { slug: data?.slug ?? `` } },
    queryKey: [`reviews-${data?.slug}`],
    enabled: data?.slug !== undefined,
    initialData: initReviewData,
    select: (sel) => sel.body,
  });

  const addRecord = (val: AttendantReviewResponse) => {
    if (!data?.slug) {
      return;
    }
    tsr.getReviews.setQueryData([`reviews-${data.slug}`], (newData) => {
      if (newData) {
        const body = newData.body;
        const findIndex = body.findIndex((df) => df.id === val.id);
        if (findIndex < 0) {
          body.unshift(val);
        } else {
          body[findIndex] = val;
        }

        return { ...newData, body: body };
      } else {
        return newData;
      }
    });
  };

  const deleteRecord = async (val: AttendantReviewResponse) => {
    const result = await reviewPrivateClient.deleteReview.mutate({
      params: { id: val.id },
    });

    if (result.status === 200) {
      if (!data?.slug) {
        return;
      }
      tsr.getReviews.setQueryData([`reviews-${data.slug}`], (newData) => {
        if (newData) {
          const body = newData.body.filter((df) => df.id !== val.id);

          return { ...newData, body: body };
        } else {
          return newData;
        }
      });
    }
  };

  const defaultTab = 'images';
  const [activeTab, setActiveTab] = useState<string>(defaultTab);

  const pathName = usePathname();

  const changeTag = useCallback(
    (val: string) => {
      setActiveTab(val);
      if (defaultTab == val) {
        history.replaceState(null, '', pathName);
      } else {
        history.replaceState(null, '', pathName + '#' + val);
      }
    },
    [activeTab]
  );

  useEffect(() => {
    const currentHash = window.location.hash
      .replaceAll('#', '')
      .trim()
      .toLowerCase();

    if (isEmpty(currentHash)) {
      setActiveTab(defaultTab);
    } else {
      setActiveTab(currentHash);
    }
  }, []);

  const [openReviewState, setOpenReviewState] = useState<
    AttendantReviewResponse | null | undefined
  >(undefined);

  return (
    <div
      className={cn(
        'mx-auto mb-8 flex w-full max-w-screen-xl flex-col p-0 lg:my-8 lg:px-8',
        {
          'pb-[calc(50px_+_env(safe-area-inset-bottom,0px))] pt-[calc(var(--header-height)_+_env(safe-area-inset-top,0px))]':
            isPwa,
        }
      )}
    >
      {data && (
        <RequestModal data={data} open={open} setOpen={setOpen}></RequestModal>
      )}

      <div className='grid w-full grid-cols-1 gap-4 lg:grid-cols-[30%_1fr] lg:gap-8 xl:gap-12'>
        <GirlDesktopHeader
          totalReviews={reviewsData?.length ?? 0}
          setOpen={setOpen}
          data={data}
          totalRating={data?.totalRating ?? 0}
          changeTag={changeTag}
        ></GirlDesktopHeader>

        <div className='mx-auto w-full max-w-[950px]'>
          <Tabs
            value={activeTab}
            onValueChange={changeTag}
            className='w-full rounded-none'
          >
            <div className='scrollbar-hide mb-4 w-full overflow-hidden overflow-x-auto border-b border-input lg:mb-6'>
              <TabsList className='inline-flex h-auto w-full min-w-full justify-start rounded-none bg-transparent pb-1 shadow-none lg:w-auto'>
                <TabsTrigger
                  className='-mb-1 rounded-none border-b-2 border-transparent py-2.5 text-sm font-medium data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none'
                  value={'images'}
                >
                  <span>{attendantGalleryTitle()}</span>
                </TabsTrigger>
                {data?.state === AttendantProfileState.APPROVED && (
                  <TabsTrigger
                    className='-mb-1 rounded-none border-b-2 border-transparent py-2.5 text-sm font-medium data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none'
                    value={`about`}
                  >
                    {attendantAboutTitle()}
                  </TabsTrigger>
                )}
                <TabsTrigger
                  className='-mb-1 rounded-none border-b-2 border-transparent py-2.5 text-sm font-medium data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none'
                  value={`gos`}
                >
                  {attendantPreferencesTitle()}
                </TabsTrigger>
                <TabsTrigger
                  className='-mb-1 rounded-none border-b-2 border-transparent py-2.5 text-sm font-medium data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none'
                  value={`prices`}
                >
                  {attendantFeeTitle()}
                </TabsTrigger>

                <TabsTrigger
                  className='-mb-1 rounded-none border-b-2 border-transparent py-2.5 text-sm font-medium data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none'
                  value={`location`}
                >
                  {attendantTravelCostsTitle()}
                </TabsTrigger>
                <TabsTrigger
                  className='-mb-1 gap-1 rounded-none border-b-2 border-transparent py-2.5 text-sm font-medium data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none'
                  value={`reviews`}
                >
                  {attendantReviews()}
                  <Badge variant={'brand'}>{homeNewcomer()}</Badge>
                </TabsTrigger>
                {data?.interviews && data.interviews.length > 0 ? (
                  <TabsTrigger
                    className='-mb-1 rounded-none border-b-2 border-transparent py-2.5 text-sm font-medium data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none'
                    value={`interview`}
                  >
                    {attendantInterviewTitle()}
                  </TabsTrigger>
                ) : null}
              </TabsList>
            </div>

            <TabsContent value='images'>
              <div className='w-full px-0 lg:p-0'>
                <GirLSlideshow data={data}></GirLSlideshow>
              </div>
            </TabsContent>
            <TabsContent value='location'>
              <div className='w-full px-4 lg:p-0'>
                <GirlLocation data={data}></GirlLocation>
              </div>
            </TabsContent>
            <TabsContent value='about'>
              <div className='w-full px-4 lg:p-0'>
                <GirlAbout data={data}></GirlAbout>
              </div>
            </TabsContent>
            <TabsContent value='prices'>
              <div className='w-full px-4 lg:p-0'>
                <GirlPrices data={data}></GirlPrices>
              </div>
            </TabsContent>
            <TabsContent value='gos'>
              <div className='w-full px-4 lg:p-0'>
                <GirlGos data={data}></GirlGos>
              </div>
            </TabsContent>
            <TabsContent value='interview'>
              <div className='w-full px-4 lg:p-0'>
                <GirlInterview data={data}></GirlInterview>
              </div>
            </TabsContent>
            <TabsContent value='reviews'>
              <div className='w-full px-4 lg:p-0'>
                <GirlReviews
                  state={data?.state}
                  totalRating={data?.totalRating ?? 0}
                  setOpenReviewState={setOpenReviewState}
                  slug={data?.slug}
                  onDelete={deleteRecord}
                  isLoading={isLoading}
                  reviews={reviewsData ?? []}
                ></GirlReviews>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <style jsx>
        {`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>
      {data && data.state == AttendantProfileState.APPROVED && isAuthed && (
        <ReviewEditModal
          name={data.name}
          addRecord={addRecord}
          open={openReviewState}
          setOpen={setOpenReviewState}
          slug={data.slug}
        ></ReviewEditModal>
      )}
    </div>
  );
};

export const GirlLayout = ({
  data,
  reviewData,
}: {
  data?: AttendantSearchSingleResponse;
  reviewData?: ClientInferResponses<typeof reviewContract.getReviews, 200>;
}) => {
  return (
    <reviewClient.ReactQueryProvider>
      <LikeProvider
        disabled={!data || data.state !== AttendantProfileState.APPROVED}
        likes={data?.totalLikes}
        entity={'AttendantEntity'}
        entityId={data?.id}
      >
        <GirlLayoutInternal
          data={data}
          reviewData={reviewData}
        ></GirlLayoutInternal>
      </LikeProvider>
    </reviewClient.ReactQueryProvider>
  );
};
